import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaCertificate } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const NSCCalculator = () => {
  const [principal, setPrincipal] = useState(100000);
  const [interestRate, setInterestRate] = useState(7.7); // Current NSC rate
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, interestRate]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    // NSC: 5-year tenure, compounded annually but paid at maturity
    const tenure = 5;
    const p = Number(principal) || 0;
    const rate = (Number(interestRate) || 0) / 100;
    
    const yearlyData = [];
    let balance = p;
    
    for (let year = 1; year <= tenure; year++) {
      const interest = balance * rate;
      balance += interest;
      
      yearlyData.push({
        year,
        openingBalance: Math.round(balance - interest),
        interest: Math.round(interest),
        closingBalance: Math.round(balance),
        // Interest for years 1-4 is deemed reinvested (80C benefit)
        taxBenefit: year < tenure ? Math.round(interest) : 0,
      });
    }
    
    const maturityAmount = Math.round(balance);
    const totalInterest = maturityAmount - p;
    
    // Calculate 80C benefit (principal + reinvested interest for years 1-4)
    const totalTaxBenefit = p + yearlyData.slice(0, 4).reduce((sum, y) => sum + y.taxBenefit, 0);
    
    setResult({
      maturityAmount,
      totalInterest,
      totalTaxBenefit: Math.min(totalTaxBenefit, 150000), // 80C limit
      yearlyData,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('National Savings Certificate Report', {
      'Investment Amount': formatCurrency(principal),
      'Interest Rate': `${interestRate}% p.a.`,
      'Tenure': '5 years',
      'Maturity Amount': formatCurrency(result.maturityAmount),
      'Total Interest': formatCurrency(result.totalInterest),
      'Section 80C Benefit': formatCurrency(result.totalTaxBenefit),
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setInterestRate(7.7);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="NSC Calculator"
        description="Free NSC Calculator India - Calculate National Savings Certificate maturity. 5-year investment with tax benefits."
        keywords="NSC calculator, National Savings Certificate calculator, NSC maturity calculator, NSC interest rate, NSC tax benefit"
        canonicalPath="/nsc"
      />
      <div className="calculator-header">
        <h1><FaCertificate style={{ color: '#f59e0b' }} /> National Savings Certificate (NSC)</h1>
        <p>Calculate returns for NSC - 5 year Government savings bond</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Min: ₹1,000, No maximum limit</small>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="1000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={interestRate === '' ? '' : interestRate}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Current rate: 7.7% (Q1 FY 2024-25)</small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                <h3>Maturity Amount (5 Years)</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Invested</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest Earned</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{interestRate}% p.a.</span>
                </div>
                <div className="result-item" style={{ background: '#eff6ff' }}>
                  <span className="label">Sec 80C Tax Benefit (Max)</span>
                  <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.totalTaxBenefit)}</span>
                </div>
              </div>

              {/* Year-wise Breakdown */}
              <div className="breakup-section">
                <h4>Year-wise Growth & Tax Benefit</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Opening</th>
                      <th>Interest</th>
                      <th>Closing</th>
                      <th>80C Benefit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.yearlyData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.year}</td>
                        <td>{formatCurrency(row.openingBalance)}</td>
                        <td style={{ color: '#10b981' }}>{formatCurrency(row.interest)}</td>
                        <td style={{ fontWeight: 'bold' }}>{formatCurrency(row.closingBalance)}</td>
                        <td style={{ color: '#2563eb' }}>
                          {row.taxBenefit > 0 ? formatCurrency(row.taxBenefit) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 NSC Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>Fixed 5-year tenure</li>
              <li>Minimum investment: ₹1,000</li>
              <li>No maximum limit</li>
              <li>Interest compounded annually</li>
              <li>Maturity amount paid at the end</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Section 80C benefit on principal</li>
              <li>Reinvested interest (Years 1-4) also qualifies for 80C</li>
              <li>Can be pledged as loan collateral</li>
              <li>Available at all Post Offices</li>
              <li>Nomination facility available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NSCCalculator;
